import { debounce } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../../../app/hooks";
import { customerUsersAutocomplete } from "../../../../../features/customer/customerUsersSlice";
import {
  CustomerUserAutocompleteOption,
  mapCustomerUserToOption,
} from "./customerUsersAutocompleteUtils";

const useCustomerUsersAutocomplete = () => {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [customerUserOptions, setCustomerUserOptions] = useState<
    CustomerUserAutocompleteOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestIdRef = useRef(0);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term: string) => {
        setSearchTerm(term);
      }, 400),
    [],
  );

  const resetSearch = useMemo(
    () => () => {
      debouncedSetSearchTerm.clear();
      latestRequestIdRef.current += 1;
      setSearchTerm("");
      setCustomerUserOptions([]);
      setIsLoading(false);
    },
    [debouncedSetSearchTerm],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setCustomerUserOptions([]);
      setIsLoading(false);
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setIsLoading(true);

    const isLocalBackend =
      process.env.REACT_APP_CORE_APIGW_ENDPOINT?.includes("localhost") ?? false;

    dispatch(
      customerUsersAutocomplete({
        search: searchTerm,
        onlyActive: !isLocalBackend,
        withCompanyName: true,
      }),
    )
      .then((action) => {
        if (latestRequestIdRef.current !== requestId) return;
        if (!isFulfilled(action)) return;

        setCustomerUserOptions(action.payload.map(mapCustomerUserToOption));
      })
      .finally(() => {
        if (latestRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [dispatch, searchTerm]);

  return {
    customerUserOptions,
    isLoading,
    setSearchTerm: debouncedSetSearchTerm,
    resetSearch,
  };
};

export default useCustomerUsersAutocomplete;
